import { useState } from 'react';
import { registerUser, ApiError, loginWithGoogle, loginWithMeta } from '../../api/client';
import GoogleSignInButton from './GoogleSignInButton';
import MetaSignInButton from './MetaSignInButton';
import FlikSelect from '../../components/Select/FlikSelect';
import {setSession} from '../../auth/authStore';
import { useNavigate, useLocation } from 'react-router-dom';


const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const GENRES = ['Mujer', 'Hombre', 'No binario', 'Otro', 'Prefiero no decirlo'];

/** Password policy — every rule must pass. Kept in one place so the validator
 *  and the info tooltip stay in sync. */
const PASSWORD_RULES = [
  { key: 'length', label: 'Al menos 8 caracteres', test: (v) => v.length >= 8 },
  { key: 'upper', label: 'Una letra mayúscula (A-Z)', test: (v) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'Una letra minúscula (a-z)', test: (v) => /[a-z]/.test(v) },
  { key: 'number', label: 'Un número (0-9)', test: (v) => /\d/.test(v) },
  {
    key: 'special',
    label: 'Un carácter especial (!@#$%…)',
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

const EMPTY = {
  name: '',
  surname: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  day: '',
  month: '',
  year: '',
  genre: '',
  acceptTerms: false,
};

/** Builds an ISO `yyyy-mm-dd` string, or null if the parts aren't a real date. */
function toIsoDate({ day, month, year }) {
  const d = Number(day);
  const m = Number(month); // 1-based
  const y = Number(year);
  if (!d || !m || !y) return null;
  const date = new Date(y, m - 1, d);
  const valid =
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d;
  if (!valid) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${y}-${pad(m)}-${pad(d)}`;
}

function validate(values) {
  const errors = {};

  if (!values.username.trim()) errors.username = 'Elige un nombre de usuario.';
  else if (!/^[a-zA-Z0-9._-]{3,}$/.test(values.username.trim()))
    errors.username = 'Mínimo 3 caracteres, sin espacios.';

  if (!values.email.trim()) errors.email = 'Introduce tu correo.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()))
    errors.email = 'El correo no es válido.';

  if (!values.password) errors.password = 'Crea una contraseña.';
  else if (!PASSWORD_RULES.every((rule) => rule.test(values.password)))
    errors.password = 'La contraseña no cumple todos los requisitos.';

  if (values.confirmPassword !== values.password)
    errors.confirmPassword = 'Las contraseñas no coinciden.';

  const iso = toIsoDate(values);
  if (!values.day && !values.month && !values.year) {
    errors.birthday = 'Introduce tu fecha de nacimiento.';
  } else if (!iso) {
    errors.birthday = 'La fecha no es válida.';
  } else if (new Date(iso) >= new Date()) {
    errors.birthday = 'La fecha debe ser anterior a hoy.';
  }

  if (!values.acceptTerms)
    errors.acceptTerms = 'Debes aceptar los términos para continuar.';

  return errors;
}

/**
 * Account creation form (step 1). On success it hands the email up to the
 * parent so the activation (code) step can take over.
 */
function RegisterForm({ onRegistered, onSwitchToLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const hide = process.env.REACT_APP_META_BUTTON_HIDE === 'true';
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reveal, setReveal] = useState({ password: false, confirmPassword: false });
  const update = (field) => (event) => {
    const value =
      event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  function extractToken(payload) {
    return (
      payload?.token ||
      payload?.accessToken ||
      payload?.jwt ||
      payload?.access_token ||
      null
    );
  }

  const completeSocialLogin = async (exchange, fallbackMessage) => {
    setBanner('');
    setSubmitting(true);
    try {
      const payload = await exchange();
      const token = extractToken(payload);
      if (!token) {
        setBanner('No hemos recibido una sesión válida. Inténtalo de nuevo.');
        return;
      }
      setSession(token, { remember: false });
      navigate(from, { replace: true });
    } catch (err) {
      setBanner(
        err instanceof ApiError ? err.detail || err.message : fallbackMessage
      );
    } finally {
      setSubmitting(false);
    }
    };

    // Google (ID-token flow): GIS hands us the credential, we post it to
    // POST /users/google.
    const handleGoogleCredential = (idToken) =>
      completeSocialLogin(
        () => loginWithGoogle({ idToken }),
        'No hemos podido entrar con Google. Inténtalo de nuevo.'
      );
  
    // Meta (Facebook Login): the FB SDK hands us a user access token, we post it
    // to POST /users/meta.
    const handleMetaAccessToken = (accessToken) =>
      completeSocialLogin(
        () => loginWithMeta({ accessToken }),
        'No hemos podido entrar con Meta. Inténtalo de nuevo.'
      );

  const setGenre = (genre) =>
    setValues((prev) => ({ ...prev, genre: prev.genre === genre ? '' : genre }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBanner('');

    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    try {
      await registerUser({
        username: values.username.trim(),
        email: values.email.trim(),
        password: values.password,
        birthday: toIsoDate(values),
        genre: values.genre || null,
        name: values.name.trim() || null,
        surname: values.surname.trim() || null,
      });
      onRegistered(values.email.trim());
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.fieldErrors && Object.keys(err.fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...err.fieldErrors }));
        }
        setBanner(err.detail || err.message);
      } else {
        setBanner('Ha ocurrido un error inesperado. Inténtalo de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `AuthInput${errors[field] ? ' AuthInput--invalid' : ''}`;

  return (
    <form className="AuthForm-fields" onSubmit={handleSubmit} noValidate>
      <h2 className="AuthForm-heading">Unos datos y estás dentro</h2>

      <div className="AuthSocials">
        <GoogleSignInButton 
          onCredential={handleGoogleCredential}
          onError={setBanner}
          disabled={submitting}
        />
        {
          !hide && (<MetaSignInButton
          onAccessToken={handleMetaAccessToken}
          onError={setBanner}
          disabled={submitting}
        />)
        }
        
      </div>

      <div className="AuthDivider">o con tu correo</div>

      <div className="AuthGrid">
        <div className="AuthField">
          <label className="AuthLabel" htmlFor="reg-name">Nombre</label>
          <input
            id="reg-name"
            className="AuthInput"
            type="text"
            placeholder="Elena"
            autoComplete="given-name"
            value={values.name}
            onChange={update('name')}
          />
        </div>

        <div className="AuthField">
          <label className="AuthLabel" htmlFor="reg-surname">Apellidos</label>
          <input
            id="reg-surname"
            className="AuthInput"
            type="text"
            placeholder="Ruiz Navarro"
            autoComplete="family-name"
            value={values.surname}
            onChange={update('surname')}
          />
        </div>

        <div className="AuthField">
          <label className="AuthLabel" htmlFor="reg-username">Nombre de usuario</label>
          <div className="AuthInputWrap">
            <span className="AuthInput-prefix" aria-hidden="true">@</span>
            <input
              id="reg-username"
              className={`${inputClass('username')} AuthInput--prefixed`}
              type="text"
              placeholder="tunombre"
              autoComplete="username"
              value={values.username}
              onChange={update('username')}
            />
          </div>
          {errors.username && <span className="AuthError">{errors.username}</span>}
        </div>

        <div className="AuthField">
          <label className="AuthLabel" htmlFor="reg-email">Correo electrónico</label>
          <input
            id="reg-email"
            className={inputClass('email')}
            type="email"
            placeholder="tu@email.com"
            autoComplete="email"
            value={values.email}
            onChange={update('email')}
          />
          {errors.email && <span className="AuthError">{errors.email}</span>}
        </div>

        <div className="AuthField">
          <div className="AuthLabelRow">
            <label className="AuthLabel" htmlFor="reg-password">Contraseña</label>
            <span className="AuthInfo" tabIndex={0} aria-label="Requisitos de la contraseña">
              <span className="AuthInfo-icon" aria-hidden="true">i</span>
              <span className="AuthInfo-pop" role="tooltip">
                <span className="AuthInfo-title">La contraseña debe tener:</span>
                <ul className="AuthInfo-list">
                  {PASSWORD_RULES.map((rule) => {
                    const ok = rule.test(values.password);
                    return (
                      <li
                        key={rule.key}
                        className={`AuthInfo-item${ok ? ' AuthInfo-item--ok' : ''}`}
                      >
                        <span className="AuthInfo-check" aria-hidden="true">
                          {ok ? '✓' : '○'}
                        </span>
                        {rule.label}
                      </li>
                    );
                  })}
                </ul>
              </span>
            </span>
          </div>
          <div className="AuthInputWrap">
            <input
              id="reg-password"
              className={inputClass('password')}
              type={reveal.password ? 'text' : 'password'}
              placeholder="••••••••"
              autoComplete="new-password"
              value={values.password}
              onChange={update('password')}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
            />

              {passwordFocused && values.password && (
                <button
                  type="button"
                  className="AuthReveal"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                  setReveal((r) => ({ ...r, password: !r.password }))
                  }
                  aria-label={
                  reveal.password ? 'Ocultar contraseña' : 'Mostrar contraseña'
                  }
                >
                  {reveal.password ? 'Ocultar' : 'Ver'}
                </button>
            )}
          </div>
          {errors.password && <span className="AuthError">{errors.password}</span>}
        </div>

        <div className="AuthField">
          <label className="AuthLabel" htmlFor="reg-confirm">Confirma contraseña</label>
          <div className="AuthInputWrap">
            <input
              id="reg-confirm"
              className={inputClass('confirmPassword')}
              type={reveal.confirmPassword ? 'text' : 'password'}
              placeholder="••••••••"
              autoComplete="new-password"
              value={values.confirmPassword}
              onChange={update('confirmPassword')}
              onFocus={() => setConfirmPasswordFocused(true)}
              onBlur={() => setConfirmPasswordFocused(false)}
            />

              {confirmPasswordFocused && values.confirmPassword && (
                <button
                  type="button"
                  className="AuthReveal"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                  setReveal((r) => ({ ...r, confirmPassword: !r.confirmPassword }))
                  }
                  aria-label={
                  reveal.confirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                  }
                >
                  {reveal.confirmPassword ? 'Ocultar' : 'Ver'}
                </button>
            )}
          </div>
          {errors.confirmPassword && (
            <span className="AuthError">{errors.confirmPassword}</span>
          )}
        </div>

        <div className="AuthField AuthField--full">
          <label className="AuthLabel" htmlFor="reg-day">Fecha de nacimiento</label>
          <div className="AuthDate">
            <input
              id="reg-day"
              className={inputClass('birthday')}
              type="text"
              inputMode="numeric"
              maxLength={2}
              placeholder="DD"
              aria-label="Día"
              value={values.day}
              onChange={update('day')}
            />
            <FlikSelect
              variant="line"
              ariaLabel="Mes"
              placeholder="Mes"
              invalid={!!errors.birthday}
              value={values.month}
              onChange={(v) => update('month')({ target: { value: v } })}
              options={MONTHS.map((label, index) => ({
                value: String(index + 1),
                label,
              }))}
            />
            <input
              className={inputClass('birthday')}
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="AAAA"
              aria-label="Año"
              value={values.year}
              onChange={update('year')}
            />
          </div>
          {errors.birthday && <span className="AuthError">{errors.birthday}</span>}
        </div>

        <div className="AuthField AuthField--full">
          <span className="AuthLabel">Género</span>
          <div className="AuthPills" role="group" aria-label="Género">
            {GENRES.map((genre) => (
              <button
                key={genre}
                type="button"
                className={`AuthPill${values.genre === genre ? ' AuthPill--active' : ''}`}
                aria-pressed={values.genre === genre}
                onClick={() => setGenre(genre)}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className="AuthTerms">
        <input
          type="checkbox"
          checked={values.acceptTerms}
          onChange={update('acceptTerms')}
        />
        <span>
          Acepto los <a href="/terminos">términos</a> y la{' '}
          <a href="/privacidad">política de privacidad</a>.
        </span>
      </label>
      {errors.acceptTerms && <span className="AuthError">{errors.acceptTerms}</span>}

      {banner && <p className="AuthBanner">{banner}</p>}

      <button className="AuthSubmit" type="submit" disabled={submitting}>
        {submitting ? 'Creando cuenta…' : 'Crear cuenta →'}
      </button>

      <p className="AuthNote">
        ¿Ya tienes cuenta?{' '}
        <button type="button" className="AuthLink" onClick={onSwitchToLogin}>
          Entrar
        </button>
      </p>
    </form>
  );
}

export default RegisterForm;
